import React, { useState, useEffect } from 'react';
import { Navbar, Nav, Container, Offcanvas } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome,
  faNewspaper,
  faBars,
  faTimes,
  faLanguage
} from '@fortawesome/free-solid-svg-icons';

const Header = () => {
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [isArabic, setIsArabic] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Load custom Arabic fonts
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Black.ttf') format('truetype');
        font-weight: 900;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Bold.ttf') format('truetype');
        font-weight: 700;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Medium.ttf') format('truetype');
        font-weight: 500;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Regular.ttf') format('truetype');
        font-weight: 400;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Light.ttf') format('truetype');
        font-weight: 300;
        font-style: normal;
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Theme Colors based on the provided palette
  const theme = {
    darkEmerald: '#255853',      // Dark Emerald Teal (Primary Dark)
    emerald: '#357E77',          // Emerald Teal (Primary)
    goldenSand: '#D3BA80',       // Golden Sand (Primary Accent)
    lightGolden: '#E0CFA6',      // Light Golden Sand (Light Accent)
    charcoal: '#32373B',         // Charcoal Gray (Text)
    darkGray: '#232729',         // Dark Charcoal
    pureWhite: '#FFFFFF',        // White
    lightGray: '#F0F0F0',        // Pure White Gray
    border: '#E0CFA6',           // Light Golden Border
    hover: 'rgba(37, 88, 83, 0.1)' // Dark Emerald Teal with transparency
  };

  const getFontFamily = (weight = 400) => {
    return isArabic ? `'ITF Qomra Arabic', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif` : "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif";
  };

  const handleClose = () => setShowOffcanvas(false);
  const handleShow = () => setShowOffcanvas(true);

  const toggleLanguage = () => {
    setIsArabic(!isArabic);
  };

  return (
    <>
      <Navbar 
        bg="white" 
        expand="lg" 
        className="navbar-clean py-1" 
        sticky="top"
        style={{ 
          borderBottom: scrolled ? `2px solid ${theme.goldenSand}` : 'none',
          boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.08)' : '0 2px 10px rgba(0,0,0,0.03)',
          transition: 'all 0.3s ease',
          direction: isArabic ? 'rtl' : 'ltr',
          fontFamily: getFontFamily(),
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <Container fluid className="px-2 px-lg-4">
          {/* Logo/Brand on the left */}
          <LinkContainer to="/">
            <Navbar.Brand className="d-flex align-items-center p-0 m-0" style={{ cursor: 'pointer' }}>
              <div className="brand-logo-wrapper" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '52px',
                height: '52px',
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${theme.lightGolden}20, ${theme.goldenSand}20)`,
                marginRight: isArabic ? '-24px' : '0',
                marginLeft: isArabic ? '0' : '-4px',
                transition: 'all 0.3s ease',
              }}>
                <img 
                  src="/ksafiles.png" 
                  alt="KSA Files" 
                  style={{
                    width: '44px',
                    height: '44px',
                    objectFit: 'contain',
                    transition: 'transform 0.3s ease',
                  }}
                />
              </div>
              <div 
                className="d-flex flex-column ms-2" 
                style={{ 
                  margin: 0,
                  alignItems: isArabic ? 'flex-end' : 'flex-start'
                }}
              >
                <span 
                  className="navbar-brand-text"
                  style={{
                    fontSize: '1.3rem',
                    fontWeight: '700',
                    color: theme.darkEmerald,
                    lineHeight: '1.2',
                    margin: 0,
                    fontFamily: getFontFamily(700),
                    whiteSpace: 'nowrap',
                    letterSpacing: '-0.5px',
                  }}
                >
                  KSA Files
                </span>
                <small 
                  className="brand-subtitle"
                  style={{
                    fontSize: '0.7rem',
                    color: theme.emerald,
                    fontWeight: '500',
                    marginTop: '2px',
                    opacity: '0.85',
                    letterSpacing: isArabic ? '0' : '0.3px',
                    fontFamily: getFontFamily(500),
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                  }}
                >
                  Official Website
                </small>
              </div>
            </Navbar.Brand>
          </LinkContainer>

          {/* Mobile Menu Button */}
          

          {/* Desktop Navigation & Language Toggle */}
          <Navbar.Collapse id="main-nav">
            <div className="d-flex justify-content-between align-items-center w-100">
              {/* Navigation Links - Centered */}
              
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Offcanvas menu for mobile */}
   

      <style jsx>{`
        .nav-link-clean {
          color: ${theme.darkEmerald} !important;
          position: relative;
        }
        
        .nav-link-clean:hover, .nav-link-clean:focus {
          background-color: ${theme.hover} !important;
          color: ${theme.darkEmerald} !important;
        }
        
        .nav-link-clean::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%) scaleX(0);
          width: 70%;
          height: 2px;
          background-color: ${theme.goldenSand};
          transition: transform 0.3s ease;
        }
        
        .nav-link-clean:hover::after {
          transform: translateX(-50%) scaleX(1);
        }
        
        .close-btn:hover {
          background-color: ${theme.hover} !important;
          border-radius: 8px;
        }
        
        .navbar-toggler-clean:hover {
          background-color: ${theme.hover} !important;
        }
        
        .brand-logo-wrapper:hover img {
          transform: scale(1.05);
        }
        
        .brand-logo-wrapper:hover {
          background: linear-gradient(135deg, ${theme.lightGolden}40, ${theme.goldenSand}40) !important;
        }
        
        /* Override Bootstrap default link colors */
        .navbar-nav .nav-link {
          color: ${theme.darkEmerald} !important;
        }
        
        .offcanvas-body .nav-link {
          color: ${theme.darkEmerald} !important;
        }
        
        /* Compact layout for desktop */
        @media (min-width: 992px) {
          .navbar {
            min-height: 70px;
          }
          
          .navbar-collapse {
            flex-grow: 0 !important;
          }
        }
      `}</style>

      {/* Additional global styles to override Bootstrap */}
      <style jsx global>{`
        .navbar-light .navbar-nav .nav-link {
          color: ${theme.darkEmerald} !important;
        }
        
        .nav-link {
          color: ${theme.darkEmerald} !important;
        }
        
        .offcanvas-body .nav-link {
          color: ${theme.darkEmerald} !important;
        }
        
        .offcanvas {
          transition: transform 0.3s ease-in-out;
        }
        
        .offcanvas-backdrop.show {
          opacity: 0.7;
        }
      `}</style>
    </>
  );
};

export default Header;